import {
  Box,
  Button,
  Flex,
  Icon,
  Modal,
  MultiSelect,
  Tab,
  TableIconButton,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Tag,
  Td,
  Text,
  TextInput,
  Tr,
  useToast,
  useTranslation,
} from "@familyzone/component-library";
import React, { useEffect, useMemo, useState } from "react";
import { getYoutubeChannel, getYoutubeVideo, YoutubeCategory } from "../../../utils/api/Youtube";
import { isYoutubeNamedEntry, ObjectPool, YoutubeDisplayTypeIndex, YoutubeNamedEntry, YoutubeNamedEntryType } from "../../../types/Objects";
import { Item } from "@familyzone/component-library/dist/components/MultiSelect";
import SortSearchTable from "../../templates/SortSearchTable";
import { TableColumn } from "../../../types/table";
import Link from "../../Link";
import { InfoIconV3 } from "../../../modules/InfoIconV3";
import { useObjectStore } from "../../../storez/ObjectStore";
import { CaptureYoutubeObjectListUpdated } from "../../../utils/PostHogUtils";
import { categoryToNamedEntry, channelToNamedEntry, videoToNamedEntry } from "./YoutubeUtils";

interface AddNewObjectEntryYoutubeProps {
  isOpen: boolean;
  onClose: () => void;
  objectPool: ObjectPool;
}

/**
 * These error types should match the error messages thrown in SMAPI > `httpd/handler/youtube.go`. They are lower case to simplify comparison.
 */
enum ErrorType {
  GenericInvalidURL = "invalid url format",
  InvalidYoutubeChannelURL = "invalid youtube channel url",
  ChannelNotFound = "youtube channel not found",
  InvalidYoutubeVideoURL = "invalid youtube video url",
  VideoNotFound = "youtube video not found",
}

const AddNewObjectEntryYoutube: React.FC<AddNewObjectEntryYoutubeProps> = ({ isOpen, onClose, objectPool }) => {
  const { t } = useTranslation();

  // Channels related states
  const [inputChannel, setInputChannel] = useState("");
  const [selectedChannels, setSelectedChannels] = useState<YoutubeNamedEntry[]>([]);

  // Categories related states
  const [allCategories, setAllCategories] = useState<YoutubeCategory[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<Item[]>([]);

  // Videos related states
  const [inputVideo, setInputVideo] = useState("");
  const [selectedVideos, setSelectedVideos] = useState<YoutubeNamedEntry[]>([]);

  const [loading, setLoading] = useState<boolean>(true);
  const { successToast, warningToast, errorToast } = useToast();

  const { saveObject, getYoutubeCategories } = useObjectStore();

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categories = await getYoutubeCategories();
        setAllCategories(categories);
        setLoading(false);
      } catch (error) {
        errorToast({
          title: "There was an error fetching YouTube data.",
          description: "Please refresh the page or try again later.",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    void fetchCategories();
  }, [errorToast, getYoutubeCategories]);

  const categoriesItems = useMemo(() => {
    const items = allCategories.map((category) => ({
      value: category.id,
      text: category.name,
    }));

    items.sort((a, b) => a.text.localeCompare(b.text));
    return items;
  }, [allCategories]);

  useEffect(() => {
    if (!isOpen) return;

    setInputChannel("");
    setInputVideo("");

    const channels: YoutubeNamedEntry[] = [];
    const categories: Item[] = [];
    const videos: YoutubeNamedEntry[] = [];

    objectPool.named_entries?.forEach((item) => {
      if (!isYoutubeNamedEntry(item)) return;

      switch (item.type) {
        case YoutubeNamedEntryType.Channel:
          channels.push(item);
          break;
        case YoutubeNamedEntryType.Category:
          const category = allCategories.find((cat) => cat.id === item.id);
          if (category) {
            categories.push({ value: category.id, text: category.name });
          }
          break;
        case YoutubeNamedEntryType.Video:
          videos.push(item);
          break;
        default:
          break;
      }
    });

    setSelectedChannels(channels);
    setSelectedCategories(categories);
    setSelectedVideos(videos);
  }, [isOpen, objectPool.named_entries, allCategories]);

  const onSaveEntries = async () => {
    const entries: YoutubeNamedEntry[] = [];

    // Add selected channels
    selectedChannels.forEach((channel) => {
      entries.push(channel);
    });

    // Add selected categories
    selectedCategories.forEach((item) => {
      // TODO: Decide how to handle categories that YouTube deprecates or removes.
      // Possibly best to just exclude them and quietly drop any existing selections, which is what we're doing now.
      const categoryName = allCategories.find((cat) => cat.id === item.value)?.name || "";
      if (categoryName !== "") {
        entries.push(categoryToNamedEntry(item.value, categoryName));
      }
    });

    // Add selected videos
    selectedVideos.forEach((video) => {
      entries.push(video);
    });

    // Submit to backend
    const objectToSave = { ...objectPool };
    objectToSave.entries = []; // Entries will be repopulated from named_entries on the backend.
    objectToSave.named_entries = entries;
    objectToSave.type = YoutubeDisplayTypeIndex; // This is probably already set but just in case.

    try {
      await saveObject(objectToSave);
      successToast({
        title: "Entries updated",
        description: `The YouTube list has been updated with ${entries.length} entries.`,
        duration: 5000,
        isClosable: true,
      });

      CaptureYoutubeObjectListUpdated(objectToSave);
      onClose();
    } catch (e) {
      errorToast({
        title: "There was an error saving the YouTube entries.",
        description: "Please try again later.",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const onCategorySelect = (selected: Item[]) => {
    setSelectedCategories(selected);
  };

  const selectedCategoryPills = (
    <Flex flexWrap="wrap" maxWidth="100%" gap={8}>
      {selectedCategories.map((item) => (
        <Box key={item.value}>
          <Tag
            tagLabel={item.text ?? ""}
            key={item.value}
            isRemovable
            onRemoveClick={() => {
              setSelectedCategories(selectedCategories.filter((i) => i.value !== item.value));
            }}
            variant="dark"
            data-testid="selected-category-pill"
          />
        </Box>
      ))}
    </Flex>
  );

  const onChannelInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputChannel(event.target.value);
  };

  const onVideoInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputVideo(event.target.value);
  };

  const onChannelAddAsync = async () => {
    try {
      const channel = await getYoutubeChannel(inputChannel);

      setSelectedChannels((prevChannels) => {
        if (prevChannels.some((ch) => ch.value === channel.id)) {
          warningToast({
            title: "This channel has already been added.",
            description: "Please enter a different channel.",
            duration: 5000,
            isClosable: true,
          });
          return prevChannels;
        }

        // Only clear input if successful
        setInputChannel("");
        return [...prevChannels, channelToNamedEntry(channel.id, channel.name, inputChannel)];
      });
    } catch (error) {
      if (error instanceof Error) {
        switch (error.message.toLowerCase()) {
          case ErrorType.GenericInvalidURL:
          case ErrorType.InvalidYoutubeChannelURL:
            errorToast({
              title: "Invalid channel URL",
              description:
                "The YouTube channel could not be added because the entered URL is not valid. Please check the URL and try again.",
              duration: 5000,
              isClosable: true,
            });
            return;

          case ErrorType.ChannelNotFound:
            errorToast({
              title: "YouTube channel not found",
              description: "A YouTube channel with this URL could not be found. Please check the URL and try again.",
              duration: 5000,
              isClosable: true,
            });
            return;

          default:
            errorToast({
              title: "There was an error fetching YouTube data.",
              description: "Please refresh the page or try again later.",
              duration: 5000,
              isClosable: true,
            });
        }
      }
    }
  };

  const onChannelAdd = () => {
    void onChannelAddAsync();
  };

  const onChannelDelete = ({ id }: YoutubeNamedEntry) => {
    if (!id) return;
    setSelectedChannels((prevChannels) => prevChannels.filter((ch) => ch.id !== id));
  };

  const onVideoAddAsync = async () => {
    try {
      const video = await getYoutubeVideo(inputVideo);

      setSelectedVideos((prevVideos) => {
        if (prevVideos.some((vid) => vid.value === video.id)) {
          warningToast({
            title: "This video has already been added.",
            description: "Please enter a different video.",
            duration: 5000,
            isClosable: true,
          });
          return prevVideos;
        }

        // Only clear input if successful
        setInputVideo("");
        return [...prevVideos, videoToNamedEntry(video.id, video.name, inputVideo)];
      });
    } catch (error) {
      if (error instanceof Error) {
        switch (error.message.toLowerCase()) {
          case ErrorType.GenericInvalidURL:
          case ErrorType.InvalidYoutubeVideoURL:
            errorToast({
              title: "Invalid YouTube URL",
              description:
                "The video could not be added because the entered URL does not appear to be valid. Please check the URL and try again.",
              duration: 5000,
              isClosable: true,
            });
            return;

          case ErrorType.VideoNotFound:
            errorToast({
              title: "YouTube video not found",
              description: "A YouTube video with this URL could not be found. Please check the URL and try again.",
              duration: 5000,
              isClosable: true,
            });
            return;

          default:
            errorToast({
              title: "There was an error fetching YouTube data.",
              description: "Please refresh the page or try again later.",
              duration: 5000,
              isClosable: true,
            });
        }
      }
    }
  };

  const onVideoAdd = () => {
    void onVideoAddAsync();
  };

  const onVideoDelete = ({ id }: YoutubeNamedEntry) => {
    if (!id) return;
    setSelectedVideos((prevVideos) => prevVideos.filter((vid) => vid.id !== id));
  };

  const columnsChannel: TableColumn[] = [
    {
      headerText: t("Channel Name"),
      columnName: "name",
      sortable: true,
      searchable: false,
    },
    {
      headerText: t("URL"),
      columnName: "desc",
      sortable: true,
      searchable: false,
    },
  ];

  const columnsVideo: TableColumn[] = [
    {
      headerText: t("Video Title"),
      columnName: "title",
      sortable: true,
      searchable: false,
    },
    {
      headerText: t("URL"),
      columnName: "url",
      sortable: true,
      searchable: false,
    },
  ];

  const tableDataMapChannel = (channel: YoutubeNamedEntry, index: number): JSX.Element => {
    return (
      <Tr key={index}>
        <Td data-testid="channel-name">
          <Text fontSize={"sm"}>{channel.name}</Text>
        </Td>
        <Td data-testid="channel-url">
          <Link href={channel.desc} isExternal={true}>
            <Text>{channel.desc}</Text>
          </Link>
        </Td>
        <Td>
          <Flex justifyContent="end">
            <TableIconButton
              icon={<Icon icon="fa-trash-can" variant="solid" color="text.paragraph.light" />}
              aria-label={"Delete channel"}
              onClick={() => onChannelDelete(channel)}
              data-testid={channel.id ? `${channel.id}-delete` : "delete"}
            />
          </Flex>
        </Td>
      </Tr>
    );
  };

  const tableDataMapVideo = (video: YoutubeNamedEntry, index: number): JSX.Element => {
    return (
      <Tr key={index}>
        <Td data-testid="video-title">
          <Text fontSize={"sm"}>{video.name}</Text>
        </Td>
        <Td data-testid="video-url">
          <Link href={video.desc} isExternal={true}>
            <Text>{video.desc}</Text>
          </Link>
        </Td>
        <Td>
          <Flex justifyContent="end">
            <TableIconButton
              icon={<Icon icon="fa-trash-can" variant="solid" color="text.paragraph.light" />}
              aria-label={"Delete video"}
              onClick={() => onVideoDelete(video)}
              data-testid={video.id ? `${video.id}-delete` : "delete"}
            />
          </Flex>
        </Td>
      </Tr>
    );
  };

  const addChannelButton = (
    <Button variant="primary" onClick={onChannelAdd} data-testid="add-channel" disabled={loading || inputChannel === ""}>
      Add Channel
    </Button>
  );

  const addVideoButton = (
    <Button variant="primary" onClick={onVideoAdd} data-testid="add-video" disabled={loading || inputVideo === ""}>
      Add Video
    </Button>
  );

  const channelsPanel = (
    <TabPanel>
      <Flex direction={"column"} pt={16} gap={16}>
        <Flex direction={"column"} gap={8}>
          <Text fontWeight={500} color="text.title" display="flex" alignItems="center">
            Channel URL
            <InfoIconV3
              tooltipText="Accepted URL formats are&#10;https://www.youtube.com/channel/channel_id&#10;https://www.youtube.com/@channel_handle&#10;https://www.youtube.com/user/channel_owner"
              maxWidth="290px"
              whiteSpace="pre-wrap"
            />
          </Text>
          <Flex flexDirection={"row"} gap={8} alignItems="baseline">
            <Box data-testid="youtube-channels-select" flexGrow={1}>
              <TextInput
                aria-label="name"
                placeholder={"Enter a Channel URL"}
                value={inputChannel}
                onChange={onChannelInputChange}
                isDisabled={loading}
              />
            </Box>
            {addChannelButton}
          </Flex>
        </Flex>
        <Flex direction={"column"}>
          <Text fontWeight={500} color="text.title">
            Added Channels
          </Text>
          <SortSearchTable
            showSearch={false}
            loaded={!loading}
            columns={columnsChannel}
            data={selectedChannels}
            tableDataMap={tableDataMapChannel}
            tableTopMargin={"0"}
            tableMargin={"0"}
            tablePadding={"0"}
          />
        </Flex>
      </Flex>
    </TabPanel>
  );

  const categoriesPanel = (
    <TabPanel>
      <Box minW={200} minH={300}>
        <Flex direction={"column"} py={16} gap={16}>
          <Text fontWeight={500} color="text.title">
            Select Categories
          </Text>
          <Box data-testid="youtube-categories-select">
            <MultiSelect
              placeholder="Select Categories"
              items={categoriesItems}
              selectedItems={selectedCategories}
              onChange={onCategorySelect}
              label="Select Categories"
              showPills={false}
              canClearAll
            />
          </Box>
          {selectedCategoryPills}
        </Flex>
      </Box>
    </TabPanel>
  );

  const videosPanel = (
    <TabPanel>
      <Flex direction={"column"} pt={16} gap={16}>
        <Flex direction={"column"} gap={16}>
          <Text fontWeight={500} color="text.title">
            Video URL
            <InfoIconV3
              tooltipText="Accepted URL formats are&#10;https://(www.|m.)youtube.com/watch?v=video_id&#10;https://(www.|m.)youtube.com/embed/video_id&#10;https://(www.|m.)youtube.com/shorts/video_id&#10;https://(www.|m.)youtube.com/v/video_id&#10;https://(www.|m.)youtube.com/live/video_id&#10;https://youtu.be/video_id&#10;https://www.youtube-nocookie.com/embed/video_id"
              maxWidth="360px"
              whiteSpace="pre-wrap"
            />
          </Text>
          <Flex flexDirection={"row"} gap={8} alignItems="baseline">
            <Box data-testid="youtube-videos-select" flexGrow={1}>
              <TextInput
                aria-label="name"
                placeholder={"Enter a Video URL"}
                value={inputVideo}
                onChange={onVideoInputChange}
                isDisabled={loading}
              />
            </Box>
            {addVideoButton}
          </Flex>
        </Flex>
        <Flex direction={"column"}>
          <Text fontWeight={500} color="text.title">
            Added Videos
          </Text>
          <SortSearchTable
            showSearch={false}
            loaded={!loading}
            columns={columnsVideo}
            data={selectedVideos}
            tableDataMap={tableDataMapVideo}
            tableTopMargin={"0"}
            tableMargin={"0"}
            tablePadding={"0"}
          />
        </Flex>
      </Flex>
    </TabPanel>
  );

  const modalBody = (
    <Box pt={16} overflowY="auto">
      <Tabs>
        <TabList>
          <Tab data-testid="tab-channels">Channels</Tab>
          <Tab data-testid="tab-categories">Categories</Tab>
          <Tab data-testid="tab-videos">Videos</Tab>
        </TabList>
        <TabPanels>
          {channelsPanel}
          {categoriesPanel}
          {videosPanel}
        </TabPanels>
      </Tabs>
    </Box>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      headerText="Edit YouTube Entries"
      size="lg"
      children={modalBody}
      primaryCTALabel="Save Entries"
      onPrimaryCTAClick={() => void onSaveEntries()}
      primaryCTADisabled={loading}
    />
  );
};

export default AddNewObjectEntryYoutube;
